import React, { useEffect, useState } from "react"
import {
  Box,
  Typography,
  Paper,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material"
import MoreHorizIcon from "@mui/icons-material/MoreHoriz"
import { useDispatch, useSelector } from "react-redux"
import Header from "components/Header/Header"
import AppButton from "components/StyledComponents/AppButton"
import AppTextField from "components/StyledComponents/AppTextField"
import AppPagination from "components/StyledComponents/AppPagination"
import CommonModal from "components/CommonModal"
import TableNoRecords from "components/TableNoRecords"
import { createBreadcrumbs } from "helpers/breadCrumbs"
import {
  getRaffleWinnersAction,
  updateRaffleWinnerPrizeClaimedAction
} from "redux/superAdmin/actions"
import { raffleWinnersColumns } from "helpers/constant"

const CustomTable = styled(Table)(({ theme }) => ({
  backgroundColor: theme.palette.background.main,
  borderCollapse: "collapse !important",
  "& .MuiTableCell-root": {
    padding: "16px 24px 16px 0px",
    backgroundColor: theme.palette.background.main,
    borderBottom: "none",
    boxSizing: "border-box",
    "&:last-child": { paddingRight: 0 }
  },
  "& .MuiTableRow-root": {
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { borderBottom: "none" }
  },
  "& .MuiTableCell-head": {
    verticalAlign: "bottom",
    position: "sticky",
    borderBottom: `2px solid #272a2c`,
    "&:last-child": { paddingRight: 0 }
  }
}))

const RaffleWinners = () => {
  const dispatch = useDispatch()
  const { raffleWinners } = useSelector((state) => state.superAdmin)
  const { userRestaurant } = useSelector((state) => state.user)
  const [breadCrumbs, setBreadCrumbs] = useState()
  const [currentPage, setCurrentPage] = useState(1)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [selectedWinner, setSelectedWinner] = useState(null)
  const [prizeClaimedInput, setPrizeClaimedInput] = useState("")

  const ITEMS_PER_PAGE = 12
  const totalPages = Math.ceil((raffleWinners?.length || 0) / ITEMS_PER_PAGE)
  const paginated = raffleWinners?.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  useEffect(() => {
    document.title = "Raffle Winners"
    setBreadCrumbs(createBreadcrumbs())
    dispatch(getRaffleWinnersAction({ restaurantId: userRestaurant?.id }))
  }, [])

  const handleOpenEdit = (winner) => {
    setSelectedWinner(winner)
    setPrizeClaimedInput(winner?.prize_claimed || "")
    setEditDialogOpen(true)
  }

  const handleCloseEdit = () => {
    setEditDialogOpen(false)
    setSelectedWinner(null)
    setPrizeClaimedInput("")
  }

  const handleSave = () => {
    if (!selectedWinner?.id) return
    dispatch(
      updateRaffleWinnerPrizeClaimedAction({
        id: selectedWinner.id,
        prize_claimed: prizeClaimedInput,
        restaurantId: userRestaurant?.id,
        onSuccess: handleCloseEdit
      })
    )
  }

  return (
    <Box sx={{ pb: 4 }}>
      <Header title="Raffle Winners" back={true} breadCrumbs={breadCrumbs} />

      <Box sx={{ mt: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2Regular" sx={{ color: "rgba(255,255,255,0.6)" }}>
            Total Winners: {raffleWinners?.length || 0}
          </Typography>
        </Box>

        <Paper>
          <TableContainer>
            <CustomTable stickyHeader aria-label="raffle winners table">
              <TableHead>
                <TableRow>
                  {raffleWinnersColumns?.map((col, index) => (
                    <TableCell key={col.id} align={col.align} style={{ minWidth: col.minWidth }}>
                      <Typography variant="body2Regular" style={{ opacity: index === 0 ? 1 : 0.6 }}>
                        {col.label}
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginated?.length ? (
                  paginated.map((winner, index) => (
                    <TableRow key={winner.id}>
                      <TableCell align="center">
                        <Typography variant="body2Regular">
                          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                          {winner?.winner_image && (
                            <img
                              src={winner.winner_image}
                              alt={winner.winner_name}
                              style={{
                                width: 36,
                                height: 36,
                                borderRadius: "50%",
                                objectFit: "cover",
                                flexShrink: 0
                              }}
                            />
                          )}
                          <Typography variant="body2Regular">
                            {winner?.winner_name ?? "-"}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2Regular">
                          {winner?.raffle_month
                            ? new Date(winner.raffle_month).toLocaleDateString("en-US", {
                                month: "short",
                                year: "numeric"
                              })
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2Regular">
                          {winner?.prize_claimed ?? <span style={{ opacity: 0.4 }}>-</span>}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Typography variant="body2Regular">
                          {winner?.created_at
                            ? new Date(winner.created_at).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric",
                                year: "numeric"
                              })
                            : "-"}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                        <span onClick={() => handleOpenEdit(winner)} style={{ cursor: "pointer" }}>
                          <MoreHorizIcon sx={{ height: "24px", width: "24px" }} />
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableNoRecords colSpan={raffleWinnersColumns?.length} />
                )}
              </TableBody>
            </CustomTable>
          </TableContainer>
        </Paper>

        <Box sx={{ mt: 3, display: "flex", justifyContent: "end" }}>
          <AppPagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={(page) => setCurrentPage(page)}
          />
        </Box>
      </Box>

      <CommonModal open={editDialogOpen} onClose={handleCloseEdit} maxWidth="xs" hideAction={true}>
        <Typography variant="h2" className="mb-1 text-center" color="white">
          Update Prize Claimed
        </Typography>
        <Typography
          variant="body2Regular"
          sx={{ opacity: 0.6, mb: "26px", display: "block", textAlign: "center" }}>
          {selectedWinner?.winner_name ?? ""}
        </Typography>
        <AppTextField
          label=""
          isUpperCase
          value={prizeClaimedInput}
          onChange={(e) => setPrizeClaimedInput(e.target.value)}
          placeholder="$0.00"
          fullWidth
        />
        <Box sx={{ mt: "30px", display: "flex", justifyContent: "center", gap: "12px" }}>
          <AppButton
            onClick={handleSave}
            disabled={!prizeClaimedInput.trim()}
            sx={{ padding: "12px 24px" }}>
            Save
          </AppButton>
        </Box>
      </CommonModal>
    </Box>
  )
}

export default RaffleWinners
